import { createContext, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { authenticatedFetch } from '../shared/utils/authenticatedFetch';
import { useAuth } from './AuthContext';

// People Space proxy (RBAC lives here — NOT the shared Spaces proxy).
const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

interface RbacPayload {
  email: string;
  roleIds: string[];
  groupIds: string[];
  /** Google Workspace groups the user belongs to (direct memberships). */
  googleGroups: string[];
  permissions: string[];
  isCoreAdmin: boolean;
  enforcementEnabled: boolean;
}

interface RBACContextValue {
  loading: boolean;
  error: string | null;
  roleIds: string[];
  groupIds: string[];
  googleGroups: string[];
  permissions: Set<string>;
  isCoreAdmin: boolean;
  enforcementEnabled: boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  refresh: () => Promise<void>;
}

const RBACContext = createContext<RBACContextValue | null>(null);

function getEmptyPayload(): RbacPayload {
  return {
    email: '',
    roleIds: [],
    groupIds: [],
    googleGroups: [],
    permissions: [],
    isCoreAdmin: false,
    enforcementEnabled: false,
  };
}

/** Coerce an unknown JSON body into a well-typed, defaulted RbacPayload. */
function coercePayload(data: unknown): RbacPayload {
  const d = (data && typeof data === 'object' ? data : {}) as Partial<RbacPayload>;
  return {
    email: typeof d.email === 'string' ? d.email : '',
    roleIds: Array.isArray(d.roleIds) ? d.roleIds : [],
    groupIds: Array.isArray(d.groupIds) ? d.groupIds : [],
    googleGroups: Array.isArray(d.googleGroups) ? d.googleGroups : [],
    permissions: Array.isArray(d.permissions) ? d.permissions : [],
    isCoreAdmin: Boolean(d.isCoreAdmin),
    enforcementEnabled: Boolean(d.enforcementEnabled),
  };
}

export function RBACProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  // React-query owns the fetch lifecycle, so there is no manual effect/setState
  // to synchronize (which the codebase's lint rules — rightly — flag).
  const query = useQuery<RbacPayload>({
    queryKey: ['rbac-me', user?.uid ?? null],
    enabled: Boolean(user),
    queryFn: async () => {
      const response = await authenticatedFetch(`${PROXY_URL}/api/rbac/me`);
      return coercePayload(await response.json());
    },
  });

  // Pull out the stable result fields so the memo below depends on those rather
  // than the query object (whose identity changes every render).
  const { data, isPending, isSuccess, error, refetch } = query;

  const value = useMemo<RBACContextValue>(() => {
    const payload = data ?? getEmptyPayload();
    const permissionSet = new Set(payload.permissions);
    // Access is only "resolved" once /rbac/me succeeds. Until then (loading, or
    // an errored fetch) we fail CLOSED: gated permissions are denied. This
    // prevents the empty-payload default (enforcementEnabled=false → "grant
    // all") from flashing confidential UI during load or opening it up when the
    // proxy is unreachable. Server routes re-authorize regardless.
    const resolved = isSuccess;
    const checkPermission = (permission: string) => {
      if (!resolved) return false;
      // Once resolved, audit-only mode (enforcement off) grants everything.
      return !payload.enforcementEnabled || permissionSet.has('*') || permissionSet.has(permission);
    };
    return {
      loading: Boolean(user) && isPending,
      error: error instanceof Error ? error.message : null,
      roleIds: payload.roleIds,
      groupIds: payload.groupIds,
      googleGroups: payload.googleGroups,
      permissions: permissionSet,
      isCoreAdmin: payload.isCoreAdmin,
      enforcementEnabled: payload.enforcementEnabled,
      hasPermission: checkPermission,
      hasAnyPermission: (permissions: string[]) => permissions.some(checkPermission),
      refresh: async () => {
        await refetch();
      },
    };
  }, [user, data, isPending, isSuccess, error, refetch]);

  return <RBACContext.Provider value={value}>{children}</RBACContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useRBAC() {
  const context = useContext(RBACContext);
  if (!context) {
    throw new Error('useRBAC must be used within RBACProvider');
  }
  return context;
}
